import { Box, Button, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import InformationIcon from '../../../assets/icons/InformationIcon';
import Alert from '../../../components/Alert/Alert';
import { useNegotiationStateAlertContext } from '../../../components/Alerts/useNegotiationStateAlertContext';
import ACPKDebtsSummaryTable from '../../../components/NegotiationDebts/ACPKDebtsSummaryTable';
import { useSpace } from '../../../content/utils/useSpace';
import useDictionary from '../../../dictionaries/context/useDictionary';
import MarkdownRender from '../../../dictionaries/MarkdownRender';
import RenderText from '../../../dictionaries/RenderText';
import { DebtType, NegotiationOption, OutDebt } from '../../../generated/graphql';
import { formatIRate } from '../../../utils/formatIRate';
import { getCommissionPercentText } from '../../../utils/getCommissionPercentText';
import { getCustomContentKeys } from '../../../utils/getCustomContentText';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { getNormative026ContentKey } from '../../../utils/getNormative026ContentKey';
import { getWeightedRateText } from '../../../utils/getWeightedRateText';
import { useGetNegotiationExpiredActionInfo } from '../../../utils/useGetNegotiationExpiredActionInfo';
import { TranquiTheme } from '../../App/theme';
import ConfirmationPaymentTerms from './ConfirmationPaymentTerms';

interface DebtDetailsNegotiationConfirmationNegTermsProps {
  outDebt: OutDebt;
  outDebts: OutDebt[];
  negOptions: NegotiationOption[];
}

const DebtDetailsNegotiationConfirmationNegTerms:
React.FC<DebtDetailsNegotiationConfirmationNegTermsProps> = ({ outDebt, outDebts, negOptions }) => {
  const { state, negotiationOption, alternative, extra } = outDebt.negotiation!;
  const {
    isConfirmedByUser,
    isInstallmentsPlan,
    isTotalPayment,
    isExpired,
    isNanocredit,
    isPaymentPromise,
    isPartialPaymentPromise,
    isVirtualDebt,
    isPunished,
    isRejected,
    isPrepaymentIncompleted,
    isACPK,
    isScotiabank,
    isRefinance,
    isBogota,
    isCatchUp,
    isMovistarMoney,
    isClaro,
    isPra,
  } = getNegBooleans(outDebt);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const { gT } = useDictionary();

  const faqLink = gT('faq_link');
  const withPaymentText = ((isMovistarMoney && isCatchUp) || (!isPunished && isRejected
    && !isPaymentPromise && faqLink && !isTotalPayment && !isACPK)
    || (isConfirmedByUser
    && (!isNanocredit || isInstallmentsPlan)
    && (!!alternative.prePayment || isTotalPayment)
    && faqLink)
    || (isScotiabank && isRefinance)) && !(isBogota && (isPaymentPromise));

  const withFaq = !(isScotiabank
    && isRefinance)
    && !(isMovistarMoney && isCatchUp)
    && !(isBogota)
    && (isMovistarMoney); // Todo: Remove this condition when faq link to APEX is ready

  const parentDebt = outDebts.find(({ type: debtType, extra: debtExtra }) => {
    return debtType === DebtType.Virtual && debtExtra?.children?.find((ch) => ch.id === outDebt.id);
  });

  const spaceContext = {
    outDebt,
    option: negotiationOption,
    alternative,
  };
  const newTermTooltipKey = useSpace('NEG_CONFIRMATION', 'negotiation.text', spaceContext);

  const textContext = useNegotiationStateAlertContext({
    alternative,
    option: negotiationOption,
    outDebt,
    extraInfo: extra.info || undefined,
    weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
  });

  const {
    otherAvailable, noneAvailable,
  } = useGetNegotiationExpiredActionInfo({ outDebt, negOptions });

  const paymentAlertContent = useSpace(
    'NEG_CONFIRMATION',
    'payment.alert.content',
    {
      outDebt,
      option: negotiationOption,
      alternative,
      ...textContext,
      bank: outDebt.bank || undefined,
      brand: outDebt.brand || undefined,
    },
  );

  if (isExpired && (otherAvailable || noneAvailable)) return null;

  const c026Key = getNormative026ContentKey(negotiationOption, outDebt, undefined, alternative) || '';
  const contentKey = gT(c026Key, {
    bank: outDebt.bank,
    monthlyRate: formatIRate((negotiationOption?.rate || outDebt?.rate), 'm'),
    annualRate: formatIRate((negotiationOption?.rate || outDebt?.rate), 'y'),
    paymentDay: moment(outDebt.paymentDate).date(),
    commissionPercent: `${((alternative?.commissionPercent || 0) * 100).toFixed(2)}%`,
    commissionPercentBulletText: getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0),
    weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
    negType: negotiationOption.type,
  });

  const customTextKey = getCustomContentKeys({
    gT,
    negOption: negotiationOption,
    outDebt,
  });

  return (
    <>
      <ConfirmationPaymentTerms
        className="mb-2"
        negOption={negotiationOption}
        outDebt={outDebt}
        alternative={alternative}
      >
        {isVirtualDebt && (
          <ACPKDebtsSummaryTable
            className="mb-2"
            debt={parentDebt || outDebt}
            title={gT('acpk_multi_table_title_confirmation')}
            withBackground
          />
        )}
      </ConfirmationPaymentTerms>
      {withPaymentText && (
        <>
          {withFaq && (
            <Box textAlign="center" my={2}>
              <Typography variant="h3">
                ¿Cómo realizar el pago?
              </Typography>
              <Typography variant="h4">
                Conoce las preguntas frecuentes
                {' '}
                <a href={faqLink} target="_blank" rel="noreferrer">AQUÍ</a>
              </Typography>
            </Box>
          )}
          {!paymentAlertContent.visible && !isNanocredit && !isClaro && !isPra
          && (
          <Box className={classes.newTermTooltip}>
            <Box className={classes.newTermToolBox}>
              <InformationIcon />
              <Typography className={classes.newTermToolTitle}>
                <RenderText textKey="negotiation_confirmation_afterPaymentText_title" />
              </Typography>
            </Box>
            <Typography className={cn(classes.newTermToolText)}>
              <RenderText
                textKey={newTermTooltipKey.keyIf || customTextKey || ''}
                context={{ ...textContext, ...newTermTooltipKey.context }}
                isMarkdown
              />
            </Typography>
          </Box>
          )}
          {paymentAlertContent.visible
          && (
          <Box className={classes.newTermTooltip}>
            <Box className={classes.newTermToolBox}>
              <InformationIcon />
              <Typography className={classes.newTermToolTitle}>
                <RenderText textKey="negotiation_confirmation_afterPaymentText_title" />
              </Typography>
            </Box>
            <Typography className={cn(classes.newTermToolText)}>
              <MarkdownRender text={paymentAlertContent.text!} />
            </Typography>
          </Box>
          )}
        </>
      )}
      {contentKey && c026Key
      && !isRejected && !isExpired && !isPrepaymentIncompleted && (
        <Box className={cn(classes.newTermTooltip, 'mt-3')}>
          <Box className={classes.alertBox}>
            <Box>
              <InformationIcon />
            </Box>
            <Typography className={cn(classes.newTermToolText, 'ml-1')}>
              <RenderText
                textKey={c026Key}
                isMarkdown
                context={{
                  bank: outDebt.bank,
                  monthlyRate: formatIRate((negotiationOption?.rate || outDebt?.rate), 'm'),
                  annualRate: formatIRate((negotiationOption?.rate || outDebt?.rate), 'y'),
                  paymentDay: moment(outDebt.paymentDate).date(),
                  commissionPercent: `${((alternative?.commissionPercent || 0) * 100).toFixed(2)}%`,
                  commissionPercentBulletText: getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0),
                  weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
                  negType: negotiationOption.type,
                }}
              />
            </Typography>
          </Box>
        </Box>
      )}
      {(isNanocredit && isPaymentPromise && isConfirmedByUser) && (
      <Alert
        color={gT(`alert_${negotiationOption.type}_confirmation_color`) as any}
        title={gT(`alert_${negotiationOption.type}_confirmation_title`)}
        filled={false}
        icon="info"
        iconColor={gT(`alert_${negotiationOption.type}_confirmation_color`) as any}
        className={cn(classes.paymentAlert, 'mt-2')}
      >
        <RenderText
          isMarkdown
          textKey={`alert_${negotiationOption.type}_confirmation_content`}
        />
      </Alert>
      )}
      {(isPartialPaymentPromise && isConfirmedByUser && !isBogota) && (
      <Alert
        color={gT(`alert_${negotiationOption.type}_${state}_partial_confirmation_color`) as any}
        title={gT(`alert_${negotiationOption.type}_${state}_partial_confirmation_title`)}
        filled={false}
        icon="info"
        iconColor={gT(`alert_${negotiationOption.type}_${state}_partial_confirmation_color`) as any}
        className={cn(classes.paymentAlert, 'mt-2', 'mb-3')}
      >
        <RenderText
          isMarkdown
          textKey={`alert_${negotiationOption.type}_${state}_partial_confirmation_content`}
        />
      </Alert>
      )}
      {(outDebts.length > 1 && !isExpired
        && !(isBogota && isInstallmentsPlan)) && (
        <Button
          className={classes.allDebtsBtn}
          component={Link}
          to="/portal/deudas"
          data-test="cy-moreDebts-btn"
          variant="outlined"
          color="secondary"
        >
          Ver todas mis deudas
        </Button>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  descriptionText: {
    ...theme.typography.body1,
    color: theme.palette.indigo.main,
    textAlign: 'center',
  },
  alertBox: {
    display: 'flex',
  },
  allDebtsBtn: {
    color: theme.palette.lavender.main,
    textAlign: 'center',
    fontWeight: 400,
    marginTop: theme.spacing(2),
  },
  newTermTooltip: {
    color: theme.palette.pearl.main,
    backgroundColor: theme.palette.pearl.main,
    border: `solid 1px ${theme.palette.lavender.main}`,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
  },
  newTermToolBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(-0.75),
    columnGap: theme.spacing(0.5),
  },
  newTermToolTitle: {
    ...theme.typography.body1,
    color: theme.palette.indigo.main,
    fontWeight: 600,
  },
  newTermToolText: {
    fontSize: '0.9rem',
    color: theme.palette.text4.main,
    whiteSpace: 'pre-wrap',
  },
  paymentAlert: {
    color: theme.palette.indigo.main,
    '& ul': {
      paddingInlineStart: theme.spacing(2.5),
    },
  },
}));

export default DebtDetailsNegotiationConfirmationNegTerms;
